import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { chatsAPI, CreateParams } from '~/api';
import { Dictionary, Page, PaginationOptions, TChat } from '~/types';

const getAll = createAsyncThunk(
  'chat/getAll',
  async (options?: PaginationOptions) => {
    return chatsAPI.getAll(options);
  },
);

const create = createAsyncThunk('chat/create', async (params: CreateParams) => {
  return chatsAPI.create(params);
});

const remove = createAsyncThunk('chat/remove', async (chatID: string) => {
  await chatsAPI.remove(chatID);
  return chatID;
});

const addChat = (state: ChatState, action: PayloadAction<TChat>) => {
  const chat = action.payload;
  if (!state.map[chat.id]) {
    state.items.push(chat.id);
  }
  state.map[chat.id] = chat;
  const itemsForSort = state.items.map((id) => {
    const { createdAt } = state.map[id];
    return { id, ts: new Date(createdAt).getTime() };
  });
  state.items = itemsForSort.sort((a, b) => b.ts - a.ts).map((i) => i.id);
};

const addChats = (state: ChatState, action: PayloadAction<Page<TChat>>) => {
  const { items: chats } = action.payload;
  for (const chat of chats) {
    if (!state.map[chat.id]) {
      state.items.push(chat.id);
    }
    state.map[chat.id] = chat;
  }
  const itemsForSort = state.items.map((id) => {
    const { createdAt } = state.map[id];
    return { id, ts: new Date(createdAt).getTime() };
  });
  state.items = itemsForSort.sort((a, b) => b.ts - a.ts).map((i) => i.id);
  state.loading = false;
};

const removeChat = (state: ChatState, action: PayloadAction<string>) => {
  const chatID = action.payload;
  delete state.map[chatID];
  state.items = state.items.filter((id) => id !== chatID);
};

const setCurrentChat = (
  state: ChatState,
  action: PayloadAction<string | undefined>,
) => {
  state.currentChat = action.payload;
};

export type ChatState = {
  loading: boolean;
  currentChat?: string;
  map: Dictionary<TChat>;
  items: string[];
};

const initialState: ChatState = {
  loading: false,
  map: {},
  items: [],
};

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addChat,
    addChats,
    removeChat,
    setCurrentChat,
  },
  extraReducers(builder) {
    // getAll
    builder.addCase(getAll.fulfilled, addChats);
    builder.addCase(getAll.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAll.rejected, (state) => {
      state.loading = false;
    });

    // create
    builder.addCase(create.fulfilled, addChat);

    // remove
    builder.addCase(remove.fulfilled, removeChat);
  },
  selectors: {
    chats: (state) => state.items.map((chatID) => state.map[chatID]),
    currentChat: (state) => state.currentChat,
  },
});

export const {
  actions: chatActions,
  reducer: chatReducer,
  selectors: chatSelectors,
} = chatSlice;

export const chatThunks = {
  getAll,
  create,
};
